import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Check as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import uploadsApi from '../../api/uploadsApi';
import { workOrdersApi } from '../../services/api';

const WorkOrderPhotoUpload = ({
  workOrderId,
  onPhotosUploaded,
  disabled = false,
  maxFiles = 5
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [uploadData, setUploadData] = useState({
    photo_type: 'progress',
    caption: '',
    gps_latitude: '',
    gps_longitude: ''
  });

  const photoTypes = [
    { value: 'before', label: 'Before Work' },
    { value: 'progress', label: 'Work in Progress' },
    { value: 'after', label: 'After Work' },
    { value: 'issue', label: 'Issue/Problem' }
  ];

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) {
        errors.push(`File ${file.name}: Only image files are allowed`);
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`File ${file.name}: File size must be less than 10MB`);
        return;
      }
      
      validFiles.push({
        file,
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: URL.createObjectURL(file)
      });
    });
    
    if (errors.length > 0) {
      setError(errors.join('; '));
    } else {
      setError('');
    }
    
    setSelectedFiles(validFiles);
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => {
      const updated = prev.filter(file => file.id !== fileId);
      const fileToRemove = prev.find(file => file.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return updated;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one photo to upload');
      return;
    }

    setUploading(true);
    setUploadProgress({});
    setError('');
    setSuccess('');

    const uploadPromises = selectedFiles.map(fileData => {
      const destinationPath = `work-orders/${workOrderId}/photos`;
      return uploadsApi.uploadFile(
        fileData.file,
        destinationPath,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => ({ ...prev, [fileData.id]: percentCompleted }));
        }
      );
    });

    try {
      const results = await Promise.all(uploadPromises);
      const photo_urls = results.map(result => result.data.Location);

      const photoMetadata = {
        photo_urls,
        photo_type: uploadData.photo_type,
        caption: uploadData.caption,
        gps_latitude: uploadData.gps_latitude,
        gps_longitude: uploadData.gps_longitude,
      };

      const response = await workOrdersApi.addPhotos(workOrderId, photoMetadata);

      setSuccess(`Successfully uploaded ${results.length} photos`);
      
      setSelectedFiles([]);
      setUploadData({
        photo_type: 'progress',
        caption: '',
        gps_latitude: '',
        gps_longitude: ''
      });
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      if (onPhotosUploaded) {
        onPhotosUploaded(response.data.data);
      }
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Failed to upload photos:', error);
      setError(error.response?.data?.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Cleanup object URLs on unmount
  React.useEffect(() => {
    return () => {
      selectedFiles.forEach(fileData => {
        URL.revokeObjectURL(fileData.preview);
      });
    };
  }, []);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Upload Work Order Photos
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Photo Type</InputLabel>
              <Select
                name="photo_type"
                value={uploadData.photo_type}
                onChange={handleInputChange}
                label="Photo Type"
                disabled={disabled || uploading}
              >
                {photoTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="caption"
              label="Caption (Optional)"
              value={uploadData.caption}
              onChange={handleInputChange}
              fullWidth
              disabled={disabled || uploading}
              placeholder="Describe the photo..."
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="gps_latitude"
              label="GPS Latitude (Optional)"
              value={uploadData.gps_latitude}
              onChange={handleInputChange}
              fullWidth
              disabled={disabled || uploading}
              placeholder="e.g., 3.1390"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="gps_longitude"
              label="GPS Longitude (Optional)"
              value={uploadData.gps_longitude}
              onChange={handleInputChange}
              fullWidth
              disabled={disabled || uploading}
              placeholder="e.g., 101.6869"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCameraIcon />}
            disabled={disabled || uploading}
            fullWidth
          >
            Select Photos (Max {maxFiles})
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Button>
        </Box>

        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files ({selectedFiles.length}/{maxFiles})
            </Typography>
            <List>
              {selectedFiles.map((fileData) => (
                <ListItem key={fileData.id} divider>
                  <ListItemIcon>
                    <ImageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={fileData.name}
                    secondary={formatFileSize(fileData.size)}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(fileData.id)}
                      disabled={uploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {selectedFiles.length > 0 && !uploading && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              disabled={disabled || uploading}
              fullWidth
            >
              Upload {selectedFiles.length} Photo{selectedFiles.length > 1 ? 's' : ''}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkOrderPhotoUpload; 